'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { X, ShoppingCart, PackageX, Gift } from 'lucide-react'
import { formatImageURL, formatPrice, getBorderRadiusClass, filterDiscounts, getEffectivePrice, getStrikethroughPrice, getLoyaltyProductDiscount, isDiscountBetter } from '@/utils/util'
import { Button } from '@/components/elements/Button'
import { useSession } from 'next-auth/react'
import { useCart } from '@/lib/commerce/hooks'
import { Medal } from 'lucide-react'
import VariantOptionQuickAdd from './VariantOptionQuickAdd'
import { hasOptions } from '@/lib/commerce/variantOptions'

interface VariantSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  product: any
  vendor: any
  onAddToCart: (variantId: string) => void
}

export default function VariantSelectionModal({ isOpen, onClose, product, vendor, onAddToCart }: VariantSelectionModalProps) {
  const { data: session } = useSession()
  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const borderRadius = vendor?.border ? getBorderRadiusClass(vendor.border) : 'rounded-md'

  const getVariantAvailability = (variant: any) => {
    if (!product?.isTrackable) return true
    return variant.stock > 0
  }

  const getVariantImage = (variant: any) => {
    return variant?.images?.length > 0
      ? formatImageURL(variant.images[0])
      : formatImageURL('placeholder.png')
  }

  const handleVariantSelect = (variant: any) => {
    if (getVariantAvailability(variant)) {
      onAddToCart(variant.id)
      onClose()
    }
  }

  const variants = product?.variants || [product?.variant].filter(Boolean)

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 flex justify-center items-end sm:items-center sm:p-4">
          <TransitionChild
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
            enterTo="translate-y-0 sm:opacity-100 sm:scale-100"
            leave="transition ease-in-out duration-300 sm:duration-200 transform"
            leaveFrom="translate-y-0 sm:opacity-100 sm:scale-100"
            leaveTo="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
          >
            <DialogPanel className="w-full sm:max-w-lg max-h-[85vh] sm:max-h-[calc(100vh-2rem)] flex flex-col transform overflow-hidden rounded-t-lg sm:rounded-lg bg-white text-left align-middle shadow-xl transition-all">
              <div className="bg-white border-b border-gray-200 px-4 py-4 sm:border-b-0 sm:px-6 sm:pt-6 sm:pb-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium leading-6 text-foreground">
                    Төрөл сонгох
                  </h3>
                  <button
                    type="button"
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={onClose}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-2 sm:mt-4">
                  <h4 className="font-medium text-foreground text-sm sm:text-base sm:mb-2">{product?.name}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">Та хүссэн төрлөө сонгоод сагсанд нэмнэ үү</p>
                </div>
              </div>

              <div className="p-4 sm:px-6 min-h-0 sm:max-h-104 overflow-y-auto">
                <div className="space-y-3">
                  {hasOptions(product) ? (
                    <VariantOptionQuickAdd product={product} onAdd={(variantId) => { onAddToCart(variantId); onClose() }} />
                  ) : variants.map((variant: any) => {
                    const isAvailable = getVariantAvailability(variant)
                    const variantImage = getVariantImage(variant)
                    const vDiscounts = filterDiscounts(product?.discounts, !!session?.user?.id, variant.id)?.filter((d: any) => !(d.type === 'buyXgetY' && d.role === 'reward')) || []
                    const vEffective = getEffectivePrice(variant, vDiscounts)
                    const vStrikethrough = getStrikethroughPrice(variant, vDiscounts)
                    const hasVariantSale = (variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price) || (vEffective !== null && vEffective < variant.price)
                    const vShowsDiscountBadge = vDiscounts.length > 0 && isDiscountBetter(variant, vDiscounts)
                    const vLoyaltyDeal = getLoyaltyProductDiscount({ product, variant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: vShowsDiscountBadge })
                    const vDisplayEffective = vLoyaltyDeal ? Math.max(0, (vEffective ?? variant.price ?? 0) - vLoyaltyDeal.unitSaving) : vEffective
                    const vDisplayStrike = vLoyaltyDeal ? (variant.price ?? 0) : vStrikethrough

                    return (
                      <div key={variant.id} className={`border ${borderRadius} p-3 cursor-pointer transition-all ${isAvailable ? 'hover:border-gray-300 hover:shadow-sm active:bg-gray-50' : 'opacity-50 cursor-not-allowed'}`} onClick={() => handleVariantSelect(variant)}>
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                            <img src={variantImage} alt={variant.name} className={`w-14 h-14 sm:w-16 sm:h-16 object-cover ${borderRadius}`} />
                            {hasVariantSale && (
                              <div className="absolute -top-1.5 -right-1.5 px-1.5 py-1 bg-orange-600 text-white rounded-full">
                                <Gift className="w-4 h-4" strokeWidth={2.5} />
                              </div>
                            )}
                            {vLoyaltyDeal && (
                              <div className="absolute -bottom-1.5 -right-1.5 px-1.5 py-0.5 bg-violet-600 text-white rounded-full text-[10px] font-bold tabular-nums flex items-center gap-0.5">
                                <Medal className="w-3 h-3" />-{vLoyaltyDeal.percent}%
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-foreground text-sm sm:text-base truncate sm:whitespace-normal">{variant.name}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {vDisplayEffective !== null ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-sm sm:text-base font-medium sm:leading-5 sm:tracking-[.025em] ${vDisplayEffective === 0 ? 'text-green-600' : 'text-red-600'}`}>{vDisplayEffective === 0 ? 'Үнэгүй' : formatPrice(vDisplayEffective)}</span>
                                  <span className="line-through text-muted-foreground text-xs sm:text-sm sm:leading-4 sm:tracking-[.0125em]">{formatPrice(vDisplayStrike)}</span>
                                </div>
                              ) : (
                                <span>{formatPrice(variant.price)}</span>
                              )}
                            </div>
                            {product?.isTrackable && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {isAvailable ? `${variant.stock} ширхэг үлдсэн` : 'Дууссан'}
                              </div>
                            )}
                          </div>
                          <div className="shrink-0">
                            {isAvailable ? (
                              <ShoppingCart className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <PackageX className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="bg-white border-t border-gray-200 p-4 sm:border-t-0 sm:px-6 sm:pt-2 sm:pb-6">
                <Button onClick={onClose} className="w-full" variant="outline">
                  Цуцлах
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
